import { Dialog } from '@angular/cdk/dialog';
import { Component, effect, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { NavigationEnd } from '@angular/router';
import { AuthService } from '@core/authentication/auth.service';
import { isInspectorUser } from '@core/authentication/auth.utility';
import { isConstructionDrawsEnabled } from '@core/guards/construction-draws-enabled/construction-draws-enabled.guard';
import { TenantService } from '@core/services';
import { OiqLayoutService } from '@oiq-layout/layout.service';
import { getLogoType } from '@oiq-layout/logo/logo.component';
import { OiqLayoutNavItemModel } from '@oiq-layout/nav/nav-item.models';
import { EMAIL_APP_STORE } from '@v2/modules/email-app/email-app.store';
import {
  oiqUserMatchesRolesOrResource,
  V2_REPORTS_ACCESS_RESOURCE,
  V2_REPORTS_ACCESS_ROLES,
} from '@v2/v2-routing.guards';
import { map, of, skipWhile, switchMap } from 'rxjs';
import { APP_MENU_2WAY_ROLES, appMenu, appSettingsMenu } from './app.menu';
import { AppService } from './app.service';

@Component({
  selector: 'oiq-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent {
  public service = inject(AppService);
  public layout = inject(OiqLayoutService);
  private authService = inject(AuthService);
  private tenantService = inject(TenantService);
  private matDialog = inject(MatDialog);
  private dialog = inject(Dialog);
  private emailStore = inject(EMAIL_APP_STORE);

  public tenantSettings$ = this.authService.cookieChanges$.pipe(
    map(() => {
      const _user = this.authService.getUser();
      // Lender-only data - the inspector portal gets a 403 for it, which logs the user out.
      if (isInspectorUser(this.authService.userRoles)) {
        return null;
      }
      return _user && _user.tenantId ? _user.tenantId : null;
    }),
    skipWhile(tenantId => !tenantId),
    switchMap(tenantId =>
      tenantId ? this.tenantService.getSettings(this.authService.getUser().tenantId) : of({})
    )
  );

  // Observable to check if a user has one of the required roles for 2-way emails
  public has2WayEmailRole$ = this.authService.cookieChanges$.pipe(
    map(() => {
      const userRoles = this.authService.userRoles;
      const allowedRoles = APP_MENU_2WAY_ROLES.map(role => role.toString());
      return userRoles.some(role => allowedRoles.includes(role));
    })
  );

  isImpersonated = toSignal(this.service.isMimicUser$);

  public isStandaloneRoute: boolean = true;
  isV2ModuleRoute = false;
  menu: OiqLayoutNavItemModel[] = [];
  settingsMenu: OiqLayoutNavItemModel | null = null;

  private _tenantSettings: any = {};

  private isEmailAuthenticated = false;

  constructor() {
    this.service.navigationEnd$.subscribe((event: NavigationEnd) => {
      this.processV2Routes(event);
      this._processMenu(this._tenantSettings);

      // Close all dialogs when navigating to the log-out route
      if (event.urlAfterRedirects.includes('/login')) {
        this.matDialog.closeAll();
        this.dialog.closeAll();
      }
    });
    this.tenantSettings$.subscribe(tenantSettings => {
      this._tenantSettings = tenantSettings;
      this._processMenu(tenantSettings);
    });

    // Monitor email authentication state changes to update a menu
    effect(() => {
      this.isEmailAuthenticated = this.emailStore.isAuthenticated();
      this._processMenu(this._tenantSettings);
    });
  }

  private _processMenu(tenantSettings: any): void {
    const _features =
      tenantSettings && tenantSettings.features ? tenantSettings.features || {} : {};
    const user = this.authService.getUser();
    this.menu = appMenu(this.authService.userRoles, {
      userRole: this.service.userRoles$.value,
      borrowingBase:
        !!_features.BorrowingBase && this.authService.userHasAccessToResources('borrowing-base'),
      scorecards: !!_features.Scorecards,
      constructionDraws: isConstructionDrawsEnabled(_features),
      workflows: !!_features.Workflow,
      emailApp:
        !!this.isEmailAuthenticated &&
        !!_features.TwoWayEmails &&
        this.authService.userHasAccessToResources('two-way-emails') &&
        !this.isImpersonated(),
      reports: oiqUserMatchesRolesOrResource(
        user?.roles,
        V2_REPORTS_ACCESS_ROLES,
        !!user && this.authService.userHasAccessToResources(V2_REPORTS_ACCESS_RESOURCE)
      ),
      dynamicReports: !!_features.DynamicReports || false,
    });

    this.settingsMenu = appSettingsMenu(this.authService.userRoles, {
      borrowingBase:
        !!_features.BorrowingBase && this.authService.userHasAccessToResources('borrowing-base'),
      scorecards: !!_features.Scorecards,
    });
  }

  public getLogoType = getLogoType;

  public isSwitching = false;
  private _isPreviousV2 = false;
  private processV2Routes(event: NavigationEnd): void {
    const _nonLayoutRoutes = [
      '/v2/iso-portal',
      '/borrower-portal',
      '/inspector-portal',
      '/v2/error',
      '/login',
      '/resetpassword',
      '/plaid',
    ];
    const _isV2ModuleRoute =
      event.urlAfterRedirects.includes('/v2') ||
      event.urlAfterRedirects.includes('/borrower-portal') ||
      event.urlAfterRedirects.includes('/inspector-portal');
    this.isStandaloneRoute = _nonLayoutRoutes.some(_route => {
      // For an exact match on /plaid route to avoid matching /applications/{id}/plaid
      if (_route === '/plaid') {
        return (
          event.urlAfterRedirects === '/plaid' || event.urlAfterRedirects.startsWith('/plaid?')
        );
      }
      return event.urlAfterRedirects.includes(_route);
    });

    // this.isSwitching = this._isPreviousV2 != _isV2ModuleRoute;
    this._isPreviousV2 = _isV2ModuleRoute;

    // if (this.isSwitching) {
    if (_isV2ModuleRoute && !document.body.classList.contains('onyxiq-theme-v2')) {
      // Add 'onyxiq-theme-v2' when the user navigates to the v2 module
      document.body.classList.add('onyxiq-theme-v2');
      document.body.classList.remove('onyxiq-theme');
    }

    if (!_isV2ModuleRoute && document.body.classList.contains('onyxiq-theme-v2')) {
      // Restore the original class when leaving the v2 module
      document.body.classList.add('onyxiq-theme');
      document.body.classList.remove('onyxiq-theme-v2');
    }
  }
}
